import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Loader, ChevronDown } from 'lucide-react';
import ExpandableSearchBar from './ExpandableSearchBar';
import { productsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';

const CustomSelect = ({ label, value, options, onChange, placeholder, isDark, themeColor, loading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative group flex-1" ref={containerRef}>
      <label className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{label}</label>
      <div 
        onClick={() => !loading && setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border-2 rounded-2xl transition-all text-sm cursor-pointer flex items-center justify-between ${isOpen ? 'ring-2' : ''} ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-100 text-gray-800'}`}
        style={{ borderColor: value || isOpen ? themeColor : (isDark ? 'rgba(255,255,255,0.1)' : '#F3F4F6'), outline: 'none', boxShadow: isOpen ? `0 0 0 4px ${themeColor}20` : 'none' }}
      >
        <span className={`truncate ${!value ? (isDark ? 'text-gray-600' : 'text-gray-400') : ''}`}>
          {loading ? 'Loading...' : (value || placeholder)}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
      </div>
      
      {isOpen && (
        <div className={`absolute top-full left-0 w-full mt-2 rounded-2xl shadow-2xl border z-[110] max-h-60 overflow-y-auto py-2 animate-in fade-in zoom-in-95 duration-200 ${isDark ? 'bg-[#111827] border-white/10 shadow-black' : 'bg-white border-gray-100 shadow-xl'}`}>
          <div 
            onClick={() => { onChange(''); setIsOpen(false); }}
            className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${isDark ? 'hover:bg-white/5 text-gray-500' : 'hover:bg-gray-50 text-gray-400'}`}
          >
            {placeholder}
          </div>
          {options.map((opt) => (
            <div 
              key={opt}
              onClick={() => { onChange(opt); setIsOpen(false); }}
              className={`px-4 py-2.5 text-sm cursor-pointer transition-all mx-2 rounded-lg mb-0.5 ${value === opt ? 'text-white font-black' : (isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50')}`}
              style={value === opt ? { backgroundColor: themeColor } : {}}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function ProductSelectionModal({ isOpen, onClose, onSelectProduct }) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subCatLoading, setSubCatLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';

  useEffect(() => {
    if (isOpen) {
      fetchAllProducts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubCategories(selectedCategory);
    } else {
      setSubCategories([]);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory && selectedSubCategory) {
      fetchProductsByCategoryAndSubCategory(selectedCategory, selectedSubCategory);
    } else {
      setProducts(allProducts);
    }
  }, [selectedCategory, selectedSubCategory]);

  const fetchAllProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const [prodRes, kitRes] = await Promise.all([
        productsApi.getAllProducts(),
        productsApi.getActiveKits()
      ]);

      const productList = Array.isArray(prodRes) ? prodRes : (prodRes.data || []);
      const kitList = Array.isArray(kitRes) ? kitRes : (kitRes.data || []);

      // ✅ Kit items — isKit: true set karo
  const kits = kitList.map(k => ({
  ...k,
  id: `kit_${k.id || k._id}`,   // ← FIX: kit id alag karo
  productName: k.kitName || k.name,
  price: k.kitPrice || k.price,
  imageUrl: k.kitImageUrl || k.image,
  productType: 'kit'
}));

      // ✅ Normal products — isKit: false set karo
      const prods = productList.map(p => ({
  ...p,
  id: `prod_${p.id || p._id}`,   // ← FIX: product id alag karo
  productType: 'product'
}));

      const combined = [...prods, ...kits];
      setAllProducts(combined);
      setProducts(combined);
    } catch (err) {
      console.error('Error fetching active items:', err);
      setError('Failed to fetch available items');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsByCategoryAndSubCategory = async (category, subCategory) => {
    setLoading(true);
    setError(null);
    try {
      const res = await productsApi.getProductsByCategoryAndSubCategory(category, subCategory);
      const productList = Array.isArray(res) ? res : (res.data || []);

      const formatted = productList.map(p => ({
        ...p,
        id: p.id || p._id,
        productType: 'product',
        isKit: false,         // ✅ Normal products
      }));

      setProducts(formatted);
    } catch (err) {
      console.error('Error fetching products by category & subcategory:', err);
      setError('Failed to fetch products for selected category');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async (category) => {
    setSubCatLoading(true);
    try {
      const res = await productsApi.getSubCategories(category);
      const subCatList = Array.isArray(res) ? res : (res.data || []);
      setSubCategories(subCatList);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setSubCategories([]);
    } finally {
      setSubCatLoading(false);
    }
  };

  const categories = [...new Set(allProducts.map(p => p.category))].filter(Boolean);

  const filteredProducts = products.filter(product => {
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesSubCategory = !selectedSubCategory || product.subCategory === selectedSubCategory;
    const matchesSearch = !searchTerm || product.productName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSubCategory && matchesSearch && product.isActive;
  });

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSelectedSubCategory('');
  };

  // ✅ Product select handler — isKit properly pass karo
  const handleProductSelect = (product) => {
    const isKit = product.isKit === true || product.productType === 'kit';

    onSelectProduct({
  id: product.productType === 'kit' 
    ? parseInt(product.id.replace('kit_', ''))   // ← original kit id
    : parseInt(product.id.replace('prod_', '')), // ← original product id
  name: product.productName,
  weight: product.weight,
  price: product.finalPrice || product.price,
  originalPrice: product.price,
  discount: product.discountPercentage,
  image: product.imageUrl || '📦',
  category: product.category,
  subCategory: product.subCategory,
  description: product.description,
  stockQuantity: product.stockQuantity,
  isKit: product.productType === 'kit', // ← sahi rahega
});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[10000] p-4">
      <div className={`rounded-[2.5rem] shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden border transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
        
        {/* Header */}
        <div className="px-8 py-6 flex items-center justify-between border-b relative overflow-hidden flex-shrink-0" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : `${currentTheme.color}15` }}>
          <div className="absolute top-0 left-0 w-1.5 h-full" style={{ backgroundColor: currentTheme.color }} />
          <h2 className={`text-xl md:text-2xl font-black uppercase tracking-[0.1em] leading-none ${isDark ? 'text-white' : 'text-gray-800'}`}>Select product</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-2xl transition-all active:scale-95 ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className={`p-8 border-b transition-all flex-shrink-0 ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-gray-50'}`}>
          <div className="flex flex-col md:flex-row gap-6">
            <CustomSelect 
              label="Category"
              value={selectedCategory}
              options={categories}
              onChange={handleCategoryChange}
              placeholder="Please select"
              isDark={isDark}
              themeColor={currentTheme.color}
            />
            <CustomSelect 
              label="Sub Category"
              value={selectedSubCategory}
              options={subCategories.map(cat => typeof cat === 'string' ? cat : cat.subCategoryName)}
              onChange={setSelectedSubCategory}
              placeholder={subCatLoading ? 'Loading...' : 'Please select'}
              isDark={isDark}
              themeColor={currentTheme.color}
              loading={!selectedCategory || subCatLoading}
            />
          </div>

          {/* Search */}
          <div className="mt-6">
            <div className="relative group">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${isDark ? 'text-gray-600 group-focus-within:text-white' : 'text-gray-400 group-focus-within:text-teal-500'}`} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                className={`w-full pl-10 pr-4 py-2.5 border-2 rounded-xl focus:outline-none transition-all text-sm ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-white/20' : 'bg-gray-50 border-gray-100 text-gray-800 focus:border-teal-500'}`}
                style={!isDark ? {} : { borderColor: searchTerm ? 'rgba(255,255,255,0.2)' : undefined }}
              />
            </div>
          </div>
        </div>

        {/* Products List */}
        <div className="p-8 overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader className="w-10 h-10 animate-spin" style={{ color: currentTheme.color }} />
              <p className="text-xs font-black uppercase tracking-widest text-gray-500">Fetching products...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 font-bold mb-4">{error}</p>
              <button
                onClick={fetchAllProducts}
                className="px-8 py-2.5 text-white rounded-xl font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg"
                style={{ backgroundColor: currentTheme.color }}
              >
                Retry
              </button>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="space-y-4">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductSelect(product)}  // ✅ Fixed handler use karo
                  className={`flex items-center justify-between p-4 border rounded-2xl cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] ${isDark ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-white border-gray-100 hover:border-teal-500 hover:shadow-md'}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.productName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<span class="text-4xl">📦</span>';
                          }}
                        />
                      ) : (
                        <span className="text-4xl">📦</span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className={`font-black text-sm uppercase tracking-wide ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          {product.productName}
                        </h3>
                        {/* ✅ Kit badge */}
                        {(product.isKit || product.productType === 'kit') && (
                          <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                            Kit
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-black text-gray-500 uppercase tracking-widest mt-1">{product.weight}</p>
                      {product.stockQuantity !== undefined && !product.isKit && (
                        <p className={`text-[10px] font-black mt-2 px-2 py-0.5 rounded-lg w-fit ${product.stockQuantity > 0 ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600')}`}>
                          Stock: {product.stockQuantity}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-black text-emerald-500">
                      ₹{product.finalPrice || product.price}
                    </div>
                    {product.discountPercentage > 0 && (
                      <div className="flex items-center justify-end space-x-2 mt-1">
                        <span className="text-xs text-gray-500 line-through">₹{product.price}</span>
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                          {product.discountPercentage}% OFF
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 space-y-4">
              <div className="w-16 h-16 bg-gray-100/10 rounded-full flex items-center justify-center mx-auto">
                <Search className="w-8 h-8 text-gray-500" />
              </div>
              <p className="text-sm font-black uppercase tracking-widest text-gray-500">No products found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductSelectionModal;